/**
 * Appendix A.6 — Save schema v1. localStorage only, no cookies, no IndexedDB.
 * Every payload carries an envelope with a checksum; corruption deletes only the
 * affected key and regenerates defaults. The game always boots.
 */

import { fnv1a32 } from './rng';

export const SAVE_VERSION = 1;

/**
 * `lumenabyss.settings.v1` is deliberately absent. The UI's `SettingsStore`
 * (src/ui/settings-store.ts) is its single owner; a second writer here with a
 * different envelope shape made each side treat the other's payload as corrupt
 * and delete it on every boot.
 */
export const KEYS = {
  meta: 'lumenabyss.meta.v1',
  run: 'lumenabyss.run.v1',
} as const;

export interface SaveEnvelope<T> {
  save_version: number;
  written_at: string;
  checksum: number;
  payload: T;
}

export interface MetaV1 {
  save_version: number;
  achievements: Record<string, { unlocked_at: string; run_seed: number }>;
  unlocks: {
    weapons: string[];
    passives: string[];
    actives: string[];
    arenas: string[];
    diff: string[];
    cosmetics: string[];
  };
  stats: { runs: number; victories: number; best_duration_s: number };
}

export function defaultMeta(): MetaV1 {
  return {
    save_version: SAVE_VERSION,
    achievements: {},
    unlocks: { weapons: [], passives: [], actives: [], arenas: [], diff: ['diff.descent-1'], cosmetics: [] },
    stats: { runs: 0, victories: 0, best_duration_s: 0 },
  };
}

/** Canonical JSON: sorted keys, no whitespace. The checksum is over this form. */
function canonical(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return '[' + value.map(canonical).join(',') + ']';
  const keys = Object.keys(value as Record<string, unknown>).sort();
  return '{' + keys.map((k) => JSON.stringify(k) + ':' + canonical((value as Record<string, unknown>)[k])).join(',') + '}';
}

/** The checksum an envelope must carry for `payload`. Exported for fixtures and tests. */
export function checksumOf(payload: unknown): number {
  return fnv1a32(canonical(payload));
}

function storage(): Storage | null {
  try {
    const s = globalThis.localStorage;
    // Safari private mode throws on write, not on access.
    const probe = '__la_probe__';
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}

export function write<T>(key: string, payload: T, nowIso: string): boolean {
  const s = storage();
  if (!s) return false;
  const env: SaveEnvelope<T> = {
    save_version: SAVE_VERSION,
    written_at: nowIso,
    checksum: fnv1a32(canonical(payload)),
    payload,
  };
  try {
    s.setItem(key, JSON.stringify(env));
    return true;
  } catch {
    return false; // quota exceeded — never fatal
  }
}

export type ReadResult<T> = { ok: true; payload: T } | { ok: false; reason: 'missing' | 'corrupt' | 'version' };

export function read<T>(key: string): ReadResult<T> {
  const s = storage();
  if (!s) return { ok: false, reason: 'missing' };
  const raw = s.getItem(key);
  if (raw === null) return { ok: false, reason: 'missing' };
  let env: SaveEnvelope<T>;
  try {
    env = JSON.parse(raw) as SaveEnvelope<T>;
  } catch {
    s.removeItem(key);
    return { ok: false, reason: 'corrupt' };
  }
  if (typeof env !== 'object' || env === null || typeof env.save_version !== 'number') {
    s.removeItem(key);
    return { ok: false, reason: 'corrupt' };
  }
  if (env.save_version > SAVE_VERSION) {
    // Unknown future version: discard with a warning, never crash.
    s.removeItem(key);
    return { ok: false, reason: 'version' };
  }
  if (fnv1a32(canonical(env.payload)) !== env.checksum) {
    s.removeItem(key);
    return { ok: false, reason: 'corrupt' };
  }
  if (env.save_version < SAVE_VERSION) {
    const migrated = migrate(key, env.save_version, env.payload);
    if (migrated === null) {
      s.removeItem(key);
      return { ok: false, reason: 'version' };
    }
    return { ok: true, payload: migrated as T };
  }
  return { ok: true, payload: env.payload };
}

export function clear(key: string): void {
  storage()?.removeItem(key);
}

type Migration = (payload: unknown) => unknown | null;

/** Registered per key: `migrations[key][fromVersion]` upgrades to fromVersion + 1. */
const migrations: Record<string, Record<number, Migration>> = {
  [KEYS.meta]: {},
  [KEYS.run]: {},
};

export function registerMigration(key: string, fromVersion: number, fn: Migration): void {
  (migrations[key] ??= {})[fromVersion] = fn;
}

function migrate(key: string, from: number, payload: unknown): unknown | null {
  let v = from;
  let cur = payload;
  const table = migrations[key];
  while (v < SAVE_VERSION) {
    const fn = table?.[v];
    if (!fn) return null;
    const next = fn(cur);
    if (next === null) return null;
    cur = next;
    v++;
  }
  return cur;
}

/**
 * Keys whose values are open dictionaries (arbitrary runtime keys) rather than a
 * fixed schema. These are replaced wholesale; merging them key-by-key against the
 * defaults would silently drop every entry the defaults do not already name.
 */
const OPEN_MAPS = new Set(['achievements']);

function mergeInto(base: Record<string, unknown>, over: unknown): Record<string, unknown> {
  if (typeof over !== 'object' || over === null || Array.isArray(over)) return base;
  for (const [k, v] of Object.entries(over as Record<string, unknown>)) {
    if (!(k in base)) continue;
    const b = base[k];
    if (OPEN_MAPS.has(k)) {
      if (typeof v === 'object' && v !== null && !Array.isArray(v)) base[k] = v;
      continue;
    }
    if (typeof b === 'object' && b !== null && !Array.isArray(b) && typeof v === 'object' && v !== null && !Array.isArray(v)) {
      mergeInto(b as Record<string, unknown>, v);
    } else if (typeof b === typeof v || (Array.isArray(b) && Array.isArray(v))) {
      base[k] = v;
    }
  }
  return base;
}

export function loadMeta(): MetaV1 {
  const res = read<Partial<MetaV1>>(KEYS.meta);
  const base = defaultMeta();
  if (!res.ok) return base;
  return mergeInto(base as unknown as Record<string, unknown>, res.payload) as unknown as MetaV1;
}

export function saveMeta(m: MetaV1, nowIso: string): void {
  write(KEYS.meta, m, nowIso);
}
